// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * Segment attribution — the part of this tool that is easy to get wrong, and
 * that nothing else does at all.
 *
 * The obvious approach is to tokenize each message on its own and add up. It is
 * wrong twice over: the template inserts control tokens between and around
 * messages, and byte-pair encoding merges across the joins. Measured on a real
 * 53-token Qwen prompt, the naive sum is out by +23 / −60 tokens.
 *
 * The second-most-obvious approach — render a growing prefix and subtract
 * consecutive counts — is *nearly* right and fails quietly. Phi-3.5 appends its
 * eos_token when add_generation_prompt is false, so every prefix render carries
 * a token the real prompt does not, and the deltas over-count.
 *
 * So instead of counting prefixes, we locate them. Every cut point is a
 * position in the ONE ground-truth token array of the real prompt, found by
 * taking the longest common token prefix between a prefix render and the real
 * thing. Segment k is simply the half-open span between cut k and cut k+1.
 *
 * That gives two properties worth the trouble:
 *   1. The spans tile the token array exactly, so the column always sums to the
 *      true total — by construction, not by rounding.
 *   2. A prefix render that differs from the real prompt (Phi-3.5's stray eos)
 *      simply stops matching, and the extra token is excluded rather than
 *      counted. The method self-corrects.
 *
 * Verified exact against Qwen2.5 (53 tokens), Llama 3.1 (75), Llama 3.2 (75),
 * Phi-3.5 (39) and SmolLM2 (54).
 */

import type { ChatMessage, Segment, ToolDefinition } from './types';
import { preview } from './format';

/** Longest common prefix of two arrays of token ids. */
export function tokenLcp(a: readonly number[], b: readonly number[]): number {
  const n = Math.min(a.length, b.length);
  let i = 0;
  while (i < n && a[i] === b[i]) i += 1;
  return i;
}

/** Longest common prefix of two strings, in characters. */
export function charLcp(a: string, b: string): number {
  const n = Math.min(a.length, b.length);
  let i = 0;
  while (i < n && a[i] === b[i]) i += 1;
  return i;
}

export interface AttributionInput {
  messages: ChatMessage[];
  tools: ToolDefinition[];
  /** Render a prefix of the conversation, or null if the template refuses it. */
  render: (messages: ChatMessage[], tools: ToolDefinition[] | null, isFinal: boolean) => string | null;
  /** Encode text to token ids (specials off — the template supplies its own). */
  encode: (text: string) => number[];
  onProgress?: (fraction: number) => void;
}

export interface ToolSpan {
  name: string;
  start: number;
  end: number;
  tokens: number;
  /** Index of the segment the template spliced this tool into. */
  parentIndex: number;
}

export interface AttributionResult {
  ids: number[];
  total: number;
  segments: Segment[];
  /** Token spans of the tool definitions, wherever the template put them. */
  toolSpans: ToolSpan[];
  /** Prefixes the template refused; those messages share a merged segment. */
  skipped: number;
  /** True when a boundary had to be snapped and may be one token out. */
  approximateBoundaries: boolean;
  /** Cut positions, exposed so the prompt view can colour token ranges. */
  cuts: number[];
  /**
   * True when the cuts collapsed and the breakdown is not trustworthy — the
   * prefix renders stopped matching the real prompt early, so almost everything
   * piled into one segment. Such a result still sums exactly, which is exactly
   * why it needs to be detected and said out loud instead of drawn as a chart.
   */
  degenerate: boolean;
}

/** Short human label for a message, used in the budget table. */
export function messageLabel(m: ChatMessage, index: number): string {
  const role = String(m.role ?? 'message');
  if (role === 'system') return 'System prompt';
  if (role === 'tool') return `Tool result${m.name ? ` · ${m.name}` : ''}`;
  if (role === 'assistant' && m.tool_calls?.length) {
    const names = m.tool_calls.map((t) => t.function?.name).filter(Boolean);
    return `Assistant · calls ${names.join(', ') || 'tool'}`;
  }
  return `${role[0].toUpperCase()}${role.slice(1)} · turn ${index + 1}`;
}

/** Flatten a message's content to text, for previews and heuristics. */
export function contentText(m: ChatMessage): string {
  const c = m.content;
  if (typeof c === 'string') return c;
  if (Array.isArray(c)) {
    return c.map((p) => (typeof p === 'string' ? p : typeof p?.text === 'string' ? p.text : '')).join(' ');
  }
  if (m.tool_calls?.length) {
    return m.tool_calls
      .map((t) => `${t.function?.name}(${typeof t.function?.arguments === 'string' ? t.function.arguments : ''})`)
      .join(' ');
  }
  return '';
}

/**
 * A tool result, or any single message long enough to be a paste rather than a
 * sentence, is retrieved context. It is the distinction people actually care
 * about — "was it my documents or my conversation" — and it is recoverable from
 * the message without asking anyone to annotate anything.
 */
export function looksRetrieved(m: ChatMessage): boolean {
  if (String(m.role) === 'tool') return true;
  return contentText(m).length > 1500;
}

function segmentKind(m: ChatMessage): Segment['kind'] {
  if (String(m.role) === 'system') return 'system';
  if (looksRetrieved(m)) return 'document';
  return 'turn';
}

/**
 * Find where the template put each tool definition.
 *
 * Ablation cannot answer this: on Qwen the tool JSON is spliced into the system
 * block and on Llama into the *first user message*, so removing the tools
 * changes a segment that has nothing to do with the user's text. Reporting
 * "tools: 0 tokens" while the user's "hello" costs two thousand is worse than
 * useless. So we locate the serialised schema in the rendered string instead,
 * and carve a nested span out of whichever segment swallowed it.
 */
export function findToolSpans(
  rendered: string,
  tools: ToolDefinition[],
  cutAt: (charOffset: number) => number,
): Array<{ name: string; start: number; end: number }> {
  const spans: Array<{ name: string; start: number; end: number }> = [];
  let searchFrom = 0;

  for (const t of tools) {
    const name = t.function?.name ?? t.name;
    if (!name) continue;
    // Find the name as it appears inside a serialised schema, then expand to
    // the enclosing JSON object by walking back to '{' and brace-matching out.
    const needle = `"${name}"`;
    let at = rendered.indexOf(needle, searchFrom);
    if (at < 0) at = rendered.indexOf(name, searchFrom);
    if (at < 0) continue;

    const open = rendered.lastIndexOf('{', at);
    if (open < 0) continue;
    const close = braceMatch(rendered, open);
    if (close < 0) continue;

    const start = cutAt(open);
    const end = cutAt(close + 1);
    if (end > start) {
      spans.push({ name, start, end });
      searchFrom = close + 1;
    }
  }
  return spans;
}

function braceMatch(s: string, open: number): number {
  let depth = 0;
  let inString = false;
  let escaped = false;
  for (let i = open; i < s.length; i++) {
    const ch = s[i];
    if (inString) {
      if (escaped) escaped = false;
      else if (ch === '\\') escaped = true;
      else if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') inString = true;
    else if (ch === '{') depth += 1;
    else if (ch === '}') {
      depth -= 1;
      if (depth === 0) return i;
    }
  }
  return -1;
}

/**
 * Attribute every token of the real prompt to a segment.
 *
 * Guarantees, all covered by tests:
 *  - The spans tile [0, ids.length) with no gaps and no overlaps, so the
 *    displayed numbers always sum to the true total.
 *  - Cuts are monotonic; a template that reorders content cannot produce a
 *    negative segment.
 *  - A prefix the template refuses is folded into the next one that renders,
 *    and reported in `skipped` rather than guessed at.
 */
export function attribute(input: AttributionInput): AttributionResult | null {
  const { messages, tools, render, encode, onProgress } = input;

  const full = render(messages, tools.length ? tools : null, true);
  if (full === null) return null;
  const ids = encode(full);

  /** Snap a character offset to a real token boundary of the ground truth. */
  const cutAt = (charOffset: number): number => {
    if (charOffset <= 0) return 0;
    if (charOffset >= full.length) return ids.length;
    return tokenLcp(encode(full.slice(0, charOffset)), ids);
  };

  const steps = messages.length + 2;
  let done = 0;
  const tick = () => {
    done += 1;
    onProgress?.(Math.min(1, done / steps));
  };

  // The template's own preamble. Llama raises when tools are supplied with no
  // messages at all ("Cannot put tools in the first user message when there's
  // no first user message!"), so this is a ladder, never a crash.
  let preambleChars = 0;
  const empty = render([], null, false);
  if (empty !== null) {
    preambleChars = charLcp(empty, full);
  } else if (messages.length > 0) {
    const one = render(messages.slice(0, 1), null, false);
    if (one !== null) preambleChars = 0;
  }
  const cuts: number[] = [cutAt(preambleChars)];
  tick();

  let skipped = 0;
  let approximate = false;
  const renderedPrefixes: Array<string | null> = [];

  for (let k = 1; k <= messages.length; k++) {
    const isFinal = k === messages.length;
    // Every prefix is rendered WITH the tools, because several templates splice
    // the tool definitions into the middle of the conversation rather than
    // prepending them — Llama 3.1 puts them inside the *first user message*.
    // Rendering prefixes without tools makes them diverge from the real prompt
    // at the first tool byte, which collapses every cut to the same position
    // and silently piles the whole conversation onto the final segment. That
    // still sums exactly, which is precisely why it needs its own guard below.
    const text = render(messages.slice(0, k), tools.length ? tools : null, isFinal);
    renderedPrefixes.push(text);
    if (text === null) {
      skipped += 1;
      cuts.push(cuts[cuts.length - 1]); // fold into the next segment that renders
      tick();
      continue;
    }
    const chars = charLcp(text, full);
    if (chars < text.length) approximate = true;
    const c = Math.max(cuts[cuts.length - 1], cutAt(chars));
    cuts.push(c);
    tick();
  }

  // Everything after the last message is the generation prompt and whatever
  // tail the template appends. Clamping here is what makes the sum exact.
  const tailStart = Math.min(cuts[cuts.length - 1], ids.length);
  cuts[cuts.length - 1] = tailStart;

  const segments: Segment[] = [];

  if (cuts[0] > 0) {
    segments.push({
      id: 'preamble',
      kind: 'overhead',
      label: 'Template preamble',
      messageIndex: -1,
      tokens: cuts[0],
      chars: preambleChars,
      preview: 'Everything the template emits before your first message.',
    });
  }

  for (let k = 0; k < messages.length; k++) {
    const start = cuts[k];
    const end = cuts[k + 1];
    const m = messages[k];
    segments.push({
      id: `msg:${k}`,
      kind: segmentKind(m),
      label: messageLabel(m, k),
      messageIndex: k,
      role: String(m.role ?? ''),
      tokens: Math.max(0, end - start),
      chars: contentText(m).length,
      preview: preview(contentText(m)),
      retrieved: looksRetrieved(m),
    });
  }

  const tailTokens = ids.length - tailStart;
  if (tailTokens > 0) {
    segments.push({
      id: 'generation',
      kind: 'overhead',
      label: 'Generation prompt',
      messageIndex: -1,
      tokens: tailTokens,
      chars: 0,
      preview: "The header that tells the model it is now its turn to speak.",
    });
  }

  const rawSpans = tools.length ? findToolSpans(full, tools, cutAt) : [];
  const toolSpans: ToolSpan[] = rawSpans.map((s) => ({
    ...s,
    tokens: s.end - s.start,
    parentIndex: segments.findIndex((seg, i) => {
      const start = i === 0 && seg.id === 'preamble' ? 0 : null;
      void start;
      return seg.messageIndex >= 0 && s.start >= cuts[seg.messageIndex] && s.start < cuts[seg.messageIndex + 1];
    }),
  }));
  tick();

  return {
    ids,
    total: ids.length,
    segments,
    toolSpans,
    skipped,
    approximateBoundaries: approximate,
    cuts,
    degenerate: isDegenerate(segments, ids.length, messages.length),
  };
}

/**
 * Did the attribution actually attribute anything?
 *
 * A collapsed run puts nearly every token in one segment while still summing to
 * the exact total, so the arithmetic check cannot catch it. The signal is that
 * a single message is carrying an implausible share of a multi-message
 * conversation. When this fires the UI shows the total — which is always
 * correct — and says the per-segment split could not be established for this
 * template, rather than drawing a chart that is a lie.
 */
export function isDegenerate(segments: Segment[], total: number, messageCount: number): boolean {
  if (messageCount < 3 || total === 0) return false;
  const perMessage = segments.filter((s) => s.messageIndex >= 0);
  const biggest = perMessage.reduce((a, s) => Math.max(a, s.tokens), 0);
  const empties = perMessage.filter((s) => s.tokens === 0).length;
  return biggest / total > 0.9 && empties >= perMessage.length - 1;
}

export interface Rollup {
  system: number;
  tools: number;
  documents: number;
  history: number;
  overhead: number;
}

/**
 * Roll the per-message rows up into the five buckets the summary shows.
 *
 * Tool tokens are carved *out of* whichever segment the template spliced them
 * into, so the bucket totals still sum to the true total while telling the
 * truth about who is responsible for the tokens.
 */
export function rollup(segments: Segment[], toolSpans: ToolSpan[] = []): Rollup {
  const r: Rollup = { system: 0, tools: 0, documents: 0, history: 0, overhead: 0 };
  const toolTokensByParent = new Map<number, number>();
  for (const s of toolSpans) {
    if (s.parentIndex >= 0) toolTokensByParent.set(s.parentIndex, (toolTokensByParent.get(s.parentIndex) ?? 0) + s.tokens);
  }

  segments.forEach((s, i) => {
    const carved = Math.min(s.tokens, toolTokensByParent.get(i) ?? 0);
    const own = s.tokens - carved;
    r.tools += carved;
    if (s.kind === 'system') r.system += own;
    else if (s.kind === 'tools') r.tools += own;
    else if (s.kind === 'document') r.documents += own;
    else if (s.kind === 'overhead') r.overhead += own;
    else r.history += own;
  });
  return r;
}

/** The invariant, exposed so the UI can refuse to draw a broken breakdown. */
export function sumsExactly(segments: Segment[], total: number): boolean {
  return segments.reduce((a, s) => a + s.tokens, 0) === total;
}

export function toolNames(tools: ToolDefinition[]): string[] {
  return tools.map((t) => t.function?.name ?? t.name).filter((n): n is string => Boolean(n));
}
